import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { JurisdictionBadge } from './JurisdictionBadge';
import type { JurisdictionEntry } from './types';

export interface JurisdictionComboboxProps {
  /** The input's id, so a caller's own label or test can find it. */
  id?: string;
  label: string;
  /** The active catalog entries, in the server's order. */
  items: readonly JurisdictionEntry[];
  /** The chosen jurisdiction code, or '' for none. */
  value: string;
  onChange: (code: string) => void;
  /** Offer only these codes (for example the states derived from the employee's addresses). Omit to offer the whole catalog. */
  allowedCodes?: readonly string[] | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
  hint?: string | undefined;
  placeholder?: string;
  /** Shows a clear button while there is a value (an optional field). */
  clearable?: boolean | undefined;
  /** True while the catalog is loading: the field says so instead of looking empty. */
  loading?: boolean | undefined;
}

const GROUP_ORDER = ['State', 'District', 'Territory'] as const;

interface Row {
  entry: JurisdictionEntry;
  index: number;
}
interface Group {
  key: string;
  label: string;
  rows: Row[];
}

/** Lower is better. Exact code first, then a code or name that starts with the text, then a name that contains it; -1 means no match. */
function rank(entry: JurisdictionEntry, query: string): number {
  const q = query.toLowerCase();
  const code = entry.code.toLowerCase();
  const name = entry.name.toLowerCase();
  if (code === q) return 0;
  if (name.startsWith(q) || code.startsWith(q)) return 1;
  if (name.split(/\s+/).some((w) => w.startsWith(q))) return 2;
  if (name.includes(q)) return 3;
  return -1;
}

function build(items: readonly JurisdictionEntry[], query: string): Group[] {
  const q = query.trim();
  const matched = items
    .map((entry) => ({ entry, rank: q ? rank(entry, q) : 0 }))
    .filter((m) => m.rank >= 0);
  const groups: Group[] = [];
  let index = 0;
  for (const type of GROUP_ORDER) {
    const inGroup = matched.filter((m) => m.entry.type === type);
    // Stable: with a search, better matches first; the catalog order (alphabetical) breaks ties.
    if (q) inGroup.sort((a, b) => a.rank - b.rank);
    if (inGroup.length === 0) continue;
    groups.push({ key: type, label: inGroup[0]!.entry.groupLabel, rows: inGroup.map((m) => ({ entry: m.entry, index: index++ })) });
  }
  return groups;
}

/**
 * A searchable, grouped single-select for a tax jurisdiction (ARIA 1.2 combobox with a listbox of grouped options), modelled on the job-role
 * GroupedCombobox. Search matches the name or the code. Each option shows its availability badge in words, so choosing a jurisdiction never implies
 * that payroll tax calculation is certified there. Options and targets are at least 44px tall and the list is as wide as the field, so it works at 375px.
 *
 * Keyboard: ArrowDown / ArrowUp open the list and move, Home / End jump to the first / last option, Enter chooses, Escape closes (a second Escape
 * clears what was typed), Tab leaves without choosing, and typing filters the list (type-ahead). Typing never changes the saved value by itself.
 */
export function JurisdictionCombobox({
  id, label, items, value, onChange, allowedCodes, required, disabled, error, hint, placeholder = 'Search by state name or code', clearable, loading,
}: JurisdictionComboboxProps) {
  const uid = useId();
  const inputId = id ?? `${uid}-input`;
  const listId = `${uid}-list`;
  const hintId = `${uid}-hint`;
  const errorId = `${uid}-error`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [typing, setTyping] = useState(false);
  // Where the arrow keys put the cursor; null = follow the results (the first match while typing, the chosen option when the list opens).
  const [cursor, setCursor] = useState<number | null>(null);

  const offered = useMemo(() => {
    const allowed = allowedCodes ? new Set(allowedCodes) : null;
    return allowed ? items.filter((e) => allowed.has(e.code)) : items;
  }, [items, allowedCodes]);
  const groups = useMemo(() => build(offered, typing ? query : ''), [offered, query, typing]);
  const flat = useMemo(() => groups.flatMap((g) => g.rows), [groups]);
  const chosen = offered.find((e) => e.code === value) ?? items.find((e) => e.code === value);
  const display = typing ? query : (chosen?.name ?? '');

  const followed = typing && query.trim() ? 0 : flat.findIndex((r) => r.entry.code === value);
  const active = !open ? -1 : cursor !== null && cursor < flat.length ? cursor : followed;

  useEffect(() => {
    if (open && active >= 0) document.getElementById(`${uid}-opt-${active}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, active, uid]);

  function close(revert: boolean) {
    setOpen(false);
    setCursor(null);
    if (revert) {
      setTyping(false);
      setQuery('');
    }
  }

  function choose(entry: JurisdictionEntry) {
    close(true);
    onChange(entry.code);
    inputRef.current?.focus();
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) setOpen(true);
        else if (flat.length > 0) setCursor((active + 1) % flat.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) setOpen(true);
        else if (flat.length > 0) setCursor(active <= 0 ? flat.length - 1 : active - 1);
        break;
      case 'Home':
        if (open && flat.length > 0) {
          e.preventDefault();
          setCursor(0);
        }
        break;
      case 'End':
        if (open && flat.length > 0) {
          e.preventDefault();
          setCursor(flat.length - 1);
        }
        break;
      case 'Enter': {
        // A closed combobox must not submit the form around it, and an open one owns Enter.
        e.preventDefault();
        const row = open ? flat[active] : undefined;
        if (row) choose(row.entry);
        break;
      }
      case 'Escape':
        if (open) {
          e.preventDefault();
          e.stopPropagation();
          close(true);
        } else if (typing || query) {
          setTyping(false);
          setQuery('');
        }
        break;
      case 'Tab':
        if (open) close(true);
        break;
      default:
        break;
    }
  }

  const statusText = !open
    ? ''
    : flat.length === 0
      ? 'No matching jurisdiction.'
      : `${flat.length} ${flat.length === 1 ? 'option' : 'options'} available. Use the up and down arrow keys, then Enter.`;
  const describedBy = error ? errorId : hint ? hintId : undefined;
  const showClear = !!clearable && !!value && !disabled;

  return (
    <div className="ts ts-field">
      <label htmlFor={inputId} className="ts-field__label">
        {label}
        {required && (
          <span className="ts-field__req" aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </label>
      <div className="ts-combobox">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-activedescendant={open && active >= 0 ? `${uid}-opt-${active}` : undefined}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          aria-required={required || undefined}
          disabled={disabled}
          placeholder={loading ? 'Loading…' : placeholder}
          value={display}
          className={`ts-combobox__input${showClear ? ' ts-combobox__input--clearable' : ''}`}
          onClick={() => {
            if (!disabled) {
              setOpen(true);
              if (!typing) inputRef.current?.select();
            }
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setTyping(true);
            setCursor(null);
            if (!open) setOpen(true);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            if (open) close(true);
          }}
        />
        {showClear && (
          <button
            type="button"
            className="ts-combobox__clear"
            onClick={() => {
              onChange('');
              setTyping(false);
              setQuery('');
              inputRef.current?.focus();
            }}
          >
            <span aria-hidden="true">×</span>
            <span className="ts-sr-only">Clear {label}</span>
          </button>
        )}
        <div className="ts-sr-only" role="status" aria-live="polite">
          {statusText}
        </div>
        {open && (
          <div className="ts-combobox__popup" onMouseDown={(e) => e.preventDefault()} data-testid="jurisdiction-popup">
            <div id={listId} role="listbox" aria-label={`${label} options`} className="ts-combobox__list">
              {groups.map((g) => {
                const gid = `${uid}-g-${g.key}`;
                return (
                  <div key={g.key} role="group" aria-labelledby={gid} className="ts-combobox__group">
                    <div id={gid} className="ts-combobox__group-label">
                      {g.label}
                    </div>
                    {g.rows.map(({ entry, index }) => (
                      <div
                        key={entry.code}
                        id={`${uid}-opt-${index}`}
                        role="option"
                        aria-selected={entry.code === value}
                        className={`ts-combobox__option${active === index ? ' ts-combobox__option--active' : ''}`}
                        onClick={() => choose(entry)}
                      >
                        <span className="ts-combobox__name">
                          {entry.name} <span className="ts-combobox__code">({entry.code})</span>
                        </span>
                        <JurisdictionBadge availability={entry.availability} />
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
            {flat.length === 0 && (
              <p className="ts-combobox__note">{offered.length === 0 ? 'No jurisdiction is available to choose yet.' : 'No matches. Try a different name or code.'}</p>
            )}
          </div>
        )}
      </div>
      {hint && !error && (
        <p id={hintId} className="ts-field__hint">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="ts-field__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
